import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TdDialogService } from '@covalent/core/dialogs';
import { TdLoadingService } from '@covalent/core/loading';
import { distanceInWords, isEqual } from 'date-fns';
import { BehaviorSubject, combineLatest, interval, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  shareReplay,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { NavigationService } from 'src/app/core/navigation.service';
import { USER } from 'src/app/core/user.service';
import { DeleteLegConfirmationDialogComponent } from 'src/app/flight-logs/components/delete-leg-confirmation-dialog/delete-leg-confirmation-dialog.component';
import { EditFlightLogPageComponent } from 'src/app/flight-logs/components/edit-flight-log-page/edit-flight-log-page.component';
import {
  FlightLogFormBuilderContext,
  FlightLogFormHelper,
  isFixedWingOrUnknown,
} from 'src/app/flight-logs/flight-log-form-helper';
import { AircraftHttpClient } from 'src/app/shared/flight-log-api/aircraft/aircraft-client.service';
import { BaseHttpClient } from 'src/app/shared/flight-log-api/bases/base-client.service';
import { EmployeeDto } from 'src/app/shared/flight-log-api/employees/employee-client.model';
import { getFlightStartEndTimesOrDefault } from 'src/app/shared/flight-log-api/flight-logs/flight-log-client-model-helpers';
import {
  FlightAdvantage,
  flightAdvantages,
  MaintenanceSummaryDto,
} from 'src/app/shared/flight-log-api/flight-logs/flight-log-client.model';
import { FlightLogHttpClient } from 'src/app/shared/flight-log-api/flight-logs/flight-log-client.service';
import { ConfigurationClassDto } from 'src/app/shared/flight-log-api/maintenance/maintenance-client.model';
import { MaintenanceHttpClient } from 'src/app/shared/flight-log-api/maintenance/maintenance-client.service';
import {
  FlightType,
  flightTypeDisplay,
  FlightTypesExternal,
  FlightTypesNonExternal,
} from 'src/app/shared/flight-log-api/shared/shared-client.model';
import { FlightLogSummaryEntity } from 'src/store/flight-log-summary/flight-log-summary.model';
import { FlightLogEntity } from 'src/store/flight-log/flight-log.model';
import { forEachControl, getControls } from 'src/util/angular/ng-forms-helpers';
import {
  combineValidtionErrorsChanges,
  getValidationStateChangesDeep,
  getValidationStateErrorsChangesDeep,
  toggleErrors,
  ValidationState,
} from 'src/util/angular/ng-forms-validation';
import { sleep } from 'src/util/async';
import { millisecondsInMinute } from 'src/util/dateLib';
import { round } from 'src/util/math';
import { isNullOrEmpty } from 'src/util/string';
import { humanize } from 'src/util/text';
import { SelectItem } from 'src/util/ui/forms';
import { getOrElse } from 'src/util/values';
import { FlightLogFeatures, UserViewModel } from 'src/view-models/user-view-model';
import {
  AlertDialogComponent,
  AlertTypeEnum,
} from '../../../shared/components/alert-dialog/alert-dialog.component';
import { createMaintenanceSummaryFormGroup } from '../../flight-log-form.model';
import { ResetLegsConfirmationDialogComponent } from '../reset-legs-confirmation-dialog/reset-legs-confirmation-dialog.component';

type MaintenanceSummaryType = 'totals' | 'simple';

@Component({
  selector: 'fl-flight-log-form',
  templateUrl: './flight-log-form.component.html',
  styleUrls: ['./flight-log-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlightLogComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input()
  public flightLog: FlightLogEntity;
  public lastSave$ = new BehaviorSubject<Date | undefined>(undefined);

  public lastSaveDisplay$: Observable<string | undefined>;
  public flightLogCompleted$ = new Subject();
  public executing$ = new BehaviorSubject<false | 'save' | 'complete' | 'create'>(false);
  public autosaveTriggered$ = new BehaviorSubject<boolean>(false);

  public canExecute$: Observable<boolean>;
  public isDisabled$: Observable<boolean>;
  public formHelper: FlightLogFormHelper;
  public configurationClass$: Observable<ConfigurationClassDto | undefined>;

  public flightTypes: SelectItem<FlightType>[];
  public editing$ = new BehaviorSubject<boolean>(false);
  public overlappingId$: Observable<FlightLogEntity | undefined>;

  public selectedAdvantages: FlightAdvantage[] = [];
  public advantageOptions: SelectItem[] = flightAdvantages.map((a) => {
    return { value: a, label: humanize(a) };
  });

  public readOnly = false;
  private _onDestroy$ = new Subject<any>();
  private componentViewInit$ = new Subject<any>();
  public topLevelValidationErrors$: Observable<string[]>;
  public allValidationErrors$: Observable<string[]>;
  public validationState$: Observable<ValidationState>;
  public flightType$: Observable<any>;
  public previousMaintenanceTotals$: Observable<MaintenanceSummaryDto | null>;
  public maintenanceSummaryType$: Observable<MaintenanceSummaryType>;
  public isFixedWing$: Observable<boolean>;
  public canSubmitTimes$: Observable<boolean>;
  constructor(
    @Inject(USER) private _user: UserViewModel,
    private _flightLogs: FlightLogHttpClient,
    private _aircraft: AircraftHttpClient,
    private _nav: NavigationService,
    private _bases: BaseHttpClient,
    private _maintenanceClient: MaintenanceHttpClient,
    private _aircraftClient: AircraftHttpClient,
    private _router: Router,
    private _fb: FormBuilder,
    private _dialog: MatDialog,
    private _cd: ChangeDetectorRef,
    private _route: ActivatedRoute,
    private _dialogService: TdDialogService,
    private _loading: TdLoadingService,
    private _snackBar: MatSnackBar,
  ) {
    this.lastSaveDisplay$ = this.lastSave$.pipe(
      switchMap((lastSave) =>
        interval(1000).pipe(
          map(() => {
            const display =
              lastSave != null
                ? distanceInWords(new Date(), lastSave, {
                    includeSeconds: false,
                    addSuffix: true,
                  })
                : undefined;
            return display;
          }),
        ),
      ),
    );
  }

  public get form(): FormGroup {
    // tslint:disable-next-line: no-non-null-assertion
    return (this.formHelper != null ? this.formHelper.form : null)!;
  }

  public get flight(): FlightLogEntity {
    return this.formHelper.flight;
  }

  public get features(): FlightLogFeatures {
    return this._user.flightLogFeatures;
  }

  public updateAdvantages() {
    this.formHelper.advantages = this.selectedAdvantages;
  }

  public ngAfterViewInit(): void {
    this.componentViewInit$.next();
  }

  public onCrewSelected(employee: EmployeeDto) {
    this.formHelper.addCrewMemberToFlight(employee);
  }
  public getWayPoints() {
    return this.formHelper.form.controls.waypoints as FormArray;
  }
  public ngOnInit() {
    if (this.flightLog.status === 'Complete') {
      this.readOnly = true;
    }

    let ftypes: FlightType[];
    if (this.flightLog.mode === 'Simple') {
      ftypes = FlightTypesExternal;
    } else {
      ftypes = FlightTypesNonExternal;
    }

    this.flightTypes = [
      // tslint:disable-next-line: no-non-null-assertion
      { label: 'None', value: null! },
      ...ftypes.map((ft) => {
        return {
          label: flightTypeDisplay(ft),
          value: ft,
        };
      }),
    ];
    const context = new FlightLogFormBuilderContext(
      this._user,
      this.flightLog,
      this.features,
      this._fb,
    );
    this.formHelper = new FlightLogFormHelper(context);

    const aircraftId$: Observable<string> = this.formHelper.aircraftIdCtrl.valueChanges.pipe(
      // Help prevent too many calls to get the previous flight
      distinctUntilChanged(),
      filter((aircraftId) => aircraftId != null),
      debounceTime(500),
    );

    if (this.isEdit) {
      // check previous flight log of selected aircraft to
      // ensure it has been completed and if not warn the user.
      aircraftId$.pipe(takeUntil(this._onDestroy$)).subscribe(async (aircraftId) => {
        const flightTime = this.formHelper.flightTime;
        if (aircraftId != null && flightTime != null) {
          const prevFlSummary = await this._flightLogs
            .getPreviousFlightLog(aircraftId, flightTime)
            .toPromise();
          if (prevFlSummary != null && prevFlSummary.status !== 'Complete') {
            const result = await this._dialogService
              .openConfirm({
                title: 'Warning',
                message: `A prior log for this aircraft has not been completed:  "Card # ${prevFlSummary.flightSummaryId}" are you sure you want to proceed with the current log? This may affect maintenance totals.`,
                acceptButton: 'Cancel',
                cancelButton: 'Proceed',
              })
              .afterClosed()
              .toPromise();
            if (result === true) {
              this._nav.goBack();
            }
          }
        }
      });
    }

    const aircraftType$ = this.formHelper.aircraftId$.pipe(
      switchMap((id) => (id != null ? this._aircraft.getById(id) : of(null))),
      map((aircraft) => (aircraft != null ? aircraft.aircraftType : null)),
    );
    this.isFixedWing$ = aircraftType$.pipe(
      map((aircraftType) => isFixedWingOrUnknown(aircraftType)),
    );
    /******************************
     * NEW MAINTENANCE SUMMARY
     ******************************/
    this.previousMaintenanceTotals$ = combineLatest([
      aircraftId$.pipe(
        filter(
          (aircraftId) =>
            aircraftId != null &&
            this.formHelper.maintenanceSummary != null &&
            this.features.totalsMaintenanceSummary,
        ),
      ),
      this.formHelper.formValues$.pipe(
        debounceTime(1000),
        map(getFlightStartEndTimesOrDefault),
        distinctUntilChanged(
          (
            d1: { startTime: Date; endTime: Date } | undefined,
            d2: { startTime: Date; endTime: Date } | undefined,
          ) => (d1 != null && d2 != null ? isEqual(d1.startTime, d2.startTime) : d1 === d2),
        ),
        map((c) => (c == null ? undefined : c.startTime)),
      ),
    ]).pipe(
      switchMap(([aircraftId, startTime]) => {
        return this._maintenanceClient
          .getFlightLogMaintenanceParameterTotals(aircraftId, this.flightLog.id, startTime)
          .pipe(catchError(() => of(null)));
      }),
      shareReplay(),
      map((r) => {
        return r as MaintenanceSummaryDto | null;
      }),
    );
    this.maintenanceSummaryType$ = of(this.features.totalsMaintenanceSummary).pipe(
      switchMap((newEnabled: boolean) => {
        if (!newEnabled) {
          return of('simple');
        }
        return this.previousMaintenanceTotals$.pipe(
          map((r: any) => {
            return r != null ? 'totals' : 'simple';
          }),
        );
      }),
      map((t) => t as MaintenanceSummaryType),
    );

    this.previousMaintenanceTotals$
      .pipe(
        filter((r: any) => r != null && this.features.totalsMaintenanceSummary),
        // tslint:disable-next-line: no-non-null-assertion
        map((r) => r!),
        takeUntil(this._onDestroy$),
      )
      .subscribe((mxTotals: MaintenanceSummaryDto) => {
        if (this.formHelper.maintenanceSummary != null) {
          // tslint:disable-next-line: no-non-null-assertion
          const startingHobbsCtrl = this.formHelper.maintenanceSummary.get('startingHobbsReading')!;
          // tslint:disable-next-line: no-non-null-assertion
          const endingHobbsCtrl = this.formHelper.maintenanceSummary.get('endingHobbsReading')!;
          // tslint:disable-next-line: no-non-null-assertion
          const totalFlightTimeCtrl = this.formHelper.maintenanceSummary.get('totalFlightTime')!;

          startingHobbsCtrl.setValue(mxTotals.totalFlightTime);

          const endingHobbs = endingHobbsCtrl.value;
          const totalFlightTime = endingHobbs - startingHobbsCtrl.value;
          totalFlightTimeCtrl.setValue(round(totalFlightTime, 2));
        }
      });
    ///////////

    /*****************************
     * OLD MAINTENANCE SUMMARY
     *****************************/
    this.maintenanceSummaryType$
      .pipe(
        filter((t) => t === 'simple'),
        switchMap(() => aircraftId$),
        takeUntil(this._onDestroy$),
      )
      .subscribe(async (aircraftId: string) => {
        if (this.formHelper.maintenanceSummary != null) {
          const ft = this.formHelper.flightTime;
          if (ft != null) {
            const prevFlightLog = await this._flightLogs
              .getPreviousFlightLog(aircraftId, ft)
              .toPromise();
            if (
              prevFlightLog != null &&
              prevFlightLog.maintenanceSummary != null &&
              prevFlightLog.maintenanceSummary.endingHobbsReading > 0
            ) {
              // tslint:disable-next-line: no-non-null-assertion
              const startingHobbs = this.formHelper.maintenanceSummary.get('startingHobbsReading')!;
              startingHobbs.setValue(prevFlightLog.maintenanceSummary.endingHobbsReading);
            }
          }
        }
      });
    //////////

    this.validationState$ = getValidationStateChangesDeep(this.formHelper.form);
    this.allValidationErrors$ = getValidationStateErrorsChangesDeep(this.formHelper.form);
    // tslint:disable-next-line: no-non-null-assertion
    const flightType = this.form.get('flightType')!;

    this.flightType$ = combineLatest([flightType.valueChanges, flightType.statusChanges]).pipe(
      map(([value, status]) => {
        const result = { value, status };
        return result;
      }),
    );
    this.configurationClass$ = this.formHelper.aircraftId$.pipe(
      mergeMap(async (aircraftId) => {
        if (aircraftId == null || isNullOrEmpty(aircraftId)) {
          return undefined;
        } else {
          const aircraft = await this._aircraftClient.getById(aircraftId).toPromise();
          if (aircraft == null) {
            return undefined;
          }
          const configurationId = getOrElse(aircraft.configurationClassId, 'Generic');

          const configurationClass = await this._maintenanceClient
            .getConfigurationClassById(configurationId)
            .toPromise();
          return configurationClass;
        }
      }),
    );

    this.configurationClass$.pipe(takeUntil(this._onDestroy$)).subscribe((config) => {
      const mxSummary = this.form.get('maintenanceSummary');
      if (
        mxSummary != null &&
        this.flightLog.maintenanceSummary != null &&
        config != null &&
        config.maintenanceParameters.length > 0
      ) {
        this.form.removeControl('maintenanceSummary');
        const params = config.maintenanceParameters
          .map((p) => ({ [p]: 0 }))
          .reduce((cur, next) => ({ ...cur, ...next }));
        this.flightLog.maintenanceSummary.engine1.cycles = {
          ...params,
          ...this.flightLog.maintenanceSummary.engine1.cycles,
        };
        this.flightLog.maintenanceSummary.engine2.cycles = {
          ...params,
          ...this.flightLog.maintenanceSummary.engine2.cycles,
        };

        this.form.addControl(
          'maintenanceSummary',
          createMaintenanceSummaryFormGroup(context, this.flightLog.maintenanceSummary),
        );
      }
    });

    // tslint:disable-next-line: no-non-null-assertion
    this.selectedAdvantages = this.form.get('advantages')!.value;

    if (this.flightLog.companyId != null) {
      this._aircraft.getAll({ companyId: this.flightLog.companyId }).toPromise();
      this._bases.getAll({ companyId: this.flightLog.companyId }).toPromise();
    }

    const controlsToValidate = [
      this.form,
      ...getControls(this.form, ['flightSummaryId', 'baseId', 'aircraftId', 'flightType']),
    ];

    this.topLevelValidationErrors$ = combineValidtionErrorsChanges(controlsToValidate);

    this.canSubmitTimes$ = this.form.controls.entries.valueChanges.pipe(
      switchMap(
        () =>
          new Observable<boolean>((sub) => {
            let isSubmitLog: boolean | null = null;
            if (context.user.flightLogFeatures.isFlighTimeRequired(context.flightLog.companyId)) {
              isSubmitLog = true;
            } else {
              forEachControl<FormGroup>(this.getWayPoints(), (wayPoint) => {
                if (
                  wayPoint.get('arrivalTime')?.value == null &&
                  wayPoint.get('departureTime')?.value == null
                ) {
                  isSubmitLog = false;
                } else if (isSubmitLog !== false) {
                  isSubmitLog = true;
                }
              });
            }
            sub.next(isSubmitLog ?? true);
            sub.complete();
          }),
      ),
    );

    this.canExecute$ = combineLatest([
      this.formHelper.isValid$,
      this.executing$,
      this.canSubmitTimes$,
    ]).pipe(
      map(([isValid, executing, canSubmitTimes]) => {
        const canExecute = !this.readOnly && isValid && !executing;
        return canExecute && canSubmitTimes;
      }),
      distinctUntilChanged(),
    );

    this.isDisabled$ = this.canExecute$.pipe(map((canExecute) => !canExecute));

    // Configure autosave
    if (this.isEdit && !this.readOnly) {
      this.formHelper.formValues$
        .pipe(
          tap(() => {
            this.autosaveTriggered$.next(this.formHelper.form.valid);
          }),
          debounceTime(2000),
          takeUntil(this._onDestroy$),
        )
        .subscribe(async () => {
          this.autosaveTriggered$.next(this.formHelper.form.valid);
          if (this.formHelper.form.valid) {
            await this._save();
          }
        });
    }

    this.overlappingId$ = this.formHelper.formValues$.pipe(
      filter((fl) => fl.aircraftId != null && fl.mode !== 'Simple'),
      debounceTime(1000),
      map(getFlightStartEndTimesOrDefault),
      distinctUntilChanged(
        (
          d1: { startTime: Date; endTime: Date } | undefined,
          d2: { startTime: Date; endTime: Date } | undefined,
        ) =>
          d1 != null && d2 != null
            ? isEqual(d1.startTime, d2.startTime) && isEqual(d1.endTime, d2.endTime)
            : d1 === d2,
      ),
      mergeMap((startEnd: { startTime: Date; endTime: Date } | undefined) => {
        if (startEnd == null) {
          return of(undefined);
        } else {
          return of(startEnd).pipe(
            mergeMap(async (current) => {
              // Check for overlap with previous flight
              let prevFlightLogSummary: FlightLogSummaryEntity | undefined;
              let isSelf = true;
              do {
                prevFlightLogSummary = await this._flightLogs
                  // tslint:disable-next-line: no-non-null-assertion
                  .getPreviousFlightLog(this.flightLog.aircraftId!, current.startTime)
                  .toPromise();
                isSelf =
                  prevFlightLogSummary != null && prevFlightLogSummary.id === this.flightLog.id;
                if (isSelf) {
                  await sleep(1000);
                }
              } while (isSelf);

              let prevFlightLog: FlightLogEntity | undefined;
              if (prevFlightLogSummary != null) {
                // To calcualte start/end times correctly, we want to get the full flight log,
                // not the flight log summary
                prevFlightLog = await this._flightLogs.getById(prevFlightLogSummary.id).toPromise();
              }

              if (prevFlightLog != null) {
                const prevStartEnd = getFlightStartEndTimesOrDefault(prevFlightLog);
                if (prevStartEnd != null) {
                  const distanceToPrevEndTimeInMs =
                    current.startTime.getTime() - prevStartEnd.endTime.getTime();
                  const previousOverlap = distanceToPrevEndTimeInMs <= millisecondsInMinute * 6;
                  if (previousOverlap) {
                    return prevFlightLog;
                  }
                }
              }
              // Check for overlap with next flight
              let nextFlightLogSummary: FlightLogSummaryEntity | undefined;
              do {
                nextFlightLogSummary = await this._flightLogs
                  // tslint:disable-next-line: no-non-null-assertion
                  .getNextFlightLog(this.flightLog.aircraftId!, current.startTime)
                  .toPromise();
                isSelf =
                  nextFlightLogSummary != null && nextFlightLogSummary.id === this.flightLog.id;
                if (isSelf) {
                  await sleep(1000);
                }
              } while (isSelf);

              let nextFlightLog: FlightLogEntity | undefined;
              if (nextFlightLogSummary != null) {
                nextFlightLog = await this._flightLogs.getById(nextFlightLogSummary.id).toPromise();
              }

              if (nextFlightLog != null) {
                const nextStartEnd = getFlightStartEndTimesOrDefault(nextFlightLog);
                if (nextStartEnd != null) {
                  const distanceToNextStartTimeInMs =
                    nextStartEnd.startTime.getTime() - current.endTime.getTime();
                  const nextOverlap = distanceToNextStartTimeInMs < millisecondsInMinute * 6;
                  if (nextOverlap) {
                    return nextFlightLog;
                  }
                }
              }
              return undefined;
            }),
          );
        }
      }),
      distinctUntilChanged(),
    );
    this.overlappingId$.pipe(takeUntil(this._onDestroy$)).subscribe((overlappingFlight) => {
      const id =
        overlappingFlight != null
          ? overlappingFlight.flightSummaryId != null
            ? `Flight #: ${overlappingFlight.flightSummaryId}`
            : `Flight ID: ${overlappingFlight.id}`
          : undefined;
      toggleErrors(overlappingFlight != null, this.formHelper.form, {
        overlappingFlight: {
          message: `This flight overlaps with another flight's start or end time, by within 6 minutes (${id}).`,
        },
      });
    });
  }

  public async complete() {
    try {
      const flightLog: FlightLogEntity = this.form.value;
      this._loading.register();
      this.executing$.next('complete');
      await this._flightLogs.complete(flightLog).toPromise();
      this.flightLogCompleted$.next();
      this.flightLogCompleted$.complete();
      await this._router.navigate([`/flight-logs/${flightLog.id}`]);
      this._snackBar.open(`Flight log marked as complete.`, undefined, {
        duration: 3000,
      });
    } catch (err) {
      this._snackBar.open('Error marking the flight log complete.', undefined, {
        duration: 3000,
      });
      throw err;
    } finally {
      this.executing$.next(false);
      this._loading.resolveAll();
    }
  }

  public async create() {
    this._loading.register();
    try {
      const flightLog: FlightLogEntity = this.form.value;
      this.executing$.next('create');
      this._loading.register();
      await this._flightLogs.create(flightLog).toPromise();
      this._router.navigate([`/flight-logs/${flightLog.id}/edit`]);
      this._snackBar.open(`Flight log saved.`, undefined, { duration: 3000 });
    } catch (err) {
      this._snackBar.open(`There was an error saving the flight log.`, undefined, {
        duration: 3000,
      });
      throw err;
    } finally {
      this.executing$.next(false);
      this._loading.resolveAll();
    }
  }

  public async _save(): Promise<boolean> {
    try {
      const flightLog: FlightLogEntity = this.form.value;
      this.executing$.next('save');
      await this._flightLogs.update(flightLog).toPromise();
      this.lastSave$.next(new Date());
      return true;
    } catch (err) {
      this._snackBar.open('There was an error saving the flight log.', undefined, {
        duration: 3000,
      });
      return false;
    } finally {
      this.executing$.next(false);
      this.autosaveTriggered$.next(false);
    }
  }

  public back() {
    this._nav.goBack();
  }

  get isEdit() {
    return this._route.component === EditFlightLogPageComponent;
  }

  public async onLegReset() {
    const dialogRef = this._dialog.open(ResetLegsConfirmationDialogComponent);
    const result = await dialogRef.afterClosed().toPromise();
    if (result === 'reset') {
      this.formHelper.resetEntries();
      this._cd.markForCheck();
    }
  }

  public async onLegDelete(entryIndex: number) {
    const dialogRef = this._dialog.open(DeleteLegConfirmationDialogComponent);
    const result = await dialogRef.afterClosed().toPromise();
    if (result === 'delete') {
      this.formHelper.removeLogEntry(entryIndex);
      this._cd.markForCheck();
    }
  }

  public ngOnDestroy() {
    if (this.formHelper != null) {
      this.formHelper.dispose();
    }
    this._onDestroy$.complete();
  }

  get baseIdCtrl() {
    // tslint:disable-next-line: no-non-null-assertion
    return this.form.get('baseId')!;
  }

  get aircraftIdCtrl() {
    // tslint:disable-next-line: no-non-null-assertion
    return this.form.get('aircraftId')!;
  }

  public showValidationErrors(errors: string[]) {
    this._dialogService.open<AlertDialogComponent>(AlertDialogComponent, {
      data: {
        title: 'Error(s)',
        messages: errors,
        type: AlertTypeEnum.Error,
      },
    });
  }
}
